interface FacebookSDK {
  init: (config: { appId: string; autoLogAppEvents: boolean; xfbml: boolean; version: string }) => void;
  login: (callback: (response: FacebookLoginResponse) => void, options: FacebookLoginOptions) => void;
}

interface FacebookLoginResponse {
  authResponse?: {
    code: string;
  };
  status?: string;
}

interface FacebookLoginOptions {
  config_id: string;
  response_type: string;
  override_default_response_type: boolean;
  extras: {
    setup: Record<string, unknown>;
    featureType: string;
    sessionInfoVersion: string;
  };
}

declare global {
  interface Window {
    FB?: FacebookSDK;
    fbAsyncInit?: () => void;
  }
}

const SDK_SRC = 'https://connect.facebook.net/es_CL/sdk.js';
const GRAPH_VERSION = 'v24.0';

let sdkPromise: Promise<FacebookSDK> | null = null;

const loadFacebookSDK = (appId: string): Promise<FacebookSDK> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Facebook SDK can only be loaded in the browser.'));
  }
  if (window.FB) {
    return Promise.resolve(window.FB);
  }
  if (sdkPromise) {
    return sdkPromise;
  }

  sdkPromise = new Promise<FacebookSDK>((resolve, reject) => {
    window.fbAsyncInit = () => {
      if (!window.FB) {
        reject(new Error('Facebook SDK loaded but window.FB is not defined.'));
        return;
      }
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: true,
        version: GRAPH_VERSION,
      });
      resolve(window.FB);
    };

    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.src = SDK_SRC;
    script.onerror = () => {
      sdkPromise = null;
      reject(new Error('Failed to load Facebook SDK script.'));
    };
    document.head.appendChild(script);
  });

  return sdkPromise;
};

export type { FacebookLoginOptions, FacebookLoginResponse, FacebookSDK };
export default loadFacebookSDK;
